import { useRef, useState, type ReactNode } from 'react';
import { Box, Button, Text } from '@customerio/pluma-components/react';
import clsx from 'clsx';
import { isEmptyString } from '@/utils/string';
import * as styles from './index.css';

export type FileInputProps = {
  /** Called with the first selected/dropped file. */
  onFileSelected: (file: File) => void;
  /** Native file type filter, e.g. `.har`. */
  accept?: string;
  isDisabled?: boolean;
  /** Instructional text shown in the dropzone. */
  label?: string;
  /** Text for the browse button. */
  browseLabel?: string;
  /** Error message shown below the dropzone. */
  error?: string;
  /**
   * Replaces the default browse affordance — e.g. a selected-file display.
   * Drag-and-drop still works while children are shown.
   */
  children?: ReactNode;
};

/**
 * A presentational drag-and-drop / click-to-browse file input. Pluma has no
 * file-input primitive, so this fills that gap for any page that needs one.
 */
export function FileInput({
  onFileSelected,
  accept,
  isDisabled = false,
  label = 'Drag a file here or browse to upload',
  browseLabel = 'Browse',
  error,
  children,
}: FileInputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragActive, setIsDragActive] = useState(false);

  const handleFile = (files: FileList | null) => {
    const file = files?.[0];
    if (file != null) {
      onFileSelected(file);
    }
  };

  return (
    <Box>
      <Box
        className={clsx(styles.dropzone, isDragActive && styles.dragActive, isDisabled && styles.disabled)}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={() => setIsDragActive(false)}
        onDrop={(event) => {
          event.preventDefault();
          setIsDragActive(false);
          if (!isDisabled) {
            handleFile(event.dataTransfer.files);
          }
        }}
      >
        {/* oxlint-disable-next-line pluma/enforce-pluma-elements -- native file input is required for browser file picker APIs. */}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={isDisabled}
          aria-label={label}
          className={styles.hiddenInput}
          onChange={(event) => {
            handleFile(event.target.files);
            // Allow re-selecting the same file.
            event.target.value = '';
          }}
        />
        {children ?? (
          <>
            <Text color="subtle">{label}</Text>
            <Button variant="secondary" icon="upload" isDisabled={isDisabled} onClick={() => inputRef.current?.click()}>
              {browseLabel}
            </Button>
          </>
        )}
      </Box>
      {!isEmptyString(error) ? (
        <Text color="critical" size="xs" mt="050">
          {error}
        </Text>
      ) : null}
    </Box>
  );
}
